import type { ApplyCPQSelectionsOptions } from "./cpq-apply-selections";
import type { CpqLinkedDisplayAttribute } from "./configurator-attribute-utils";

/** Minimal Oracle CPQ / BigMachines JS surface used for attribute change listeners. */
export type CpqJsAttributeApi = {
  onAttributeChange?: (
    varName: string,
    handler: (payload: unknown) => void
  ) => void;
  offAttributeChange?: (
    varName: string,
    handler: (payload: unknown) => void
  ) => void;
  removeAttributeChange?: (
    varName: string,
    handler: (payload: unknown) => void
  ) => void;
  attributeExists?: (varName: string) => boolean;
  getAttributeVal?: (varName: string) => unknown;
};

export type RegisterCpqLinkedAttributeListenersHandle = {
  /** Oracle CPQ attribute that maps to a Threekit attribute via `metadata.cpq_attribute`. */
  applyCPQSelections(
    selections: Record<string, string | number>,
    options?: ApplyCPQSelectionsOptions
  ): void;
  getCpqLinkedDisplayAttributes(): CpqLinkedDisplayAttribute[];
};

export interface RegisterCpqLinkedAttributeListenersOptions {
  /**
   * Linked rows to listen on. When omitted, uses `handle.getCpqLinkedDisplayAttributes()`.
   */
  linked?: CpqLinkedDisplayAttribute[];
  /**
   * Resolve `CPQJS` (or a compatible API). Defaults to `window.CPQJS`.
   */
  getCpqJs?: () => CpqJsAttributeApi | null | undefined;
  /** Forwarded to each `applyCPQSelections` call triggered by a change. */
  applyOptions?: ApplyCPQSelectionsOptions;
}

export interface CpqLinkedAttributeListenersHandle {
  /** Unregister all listeners. */
  detach(): void;
  /**
   * Refresh the active linked set and register listeners for any new `cpqAttribute` names
   * (e.g. after a CPQ “update” rebuilds the BOM). Existing listeners are kept.
   */
  setLinkedAttributes(linked: CpqLinkedDisplayAttribute[]): void;
}

function defaultGetCpqJs(): CpqJsAttributeApi | null | undefined {
  if (typeof globalThis === "undefined") return undefined;
  const g = globalThis as unknown as { CPQJS?: CpqJsAttributeApi };
  return g.CPQJS;
}

/**
 * `null` means "ignore this change event" (no usable value at all). An empty string is
 * returned as-is — it represents a multi-select CPQ field with nothing checked, and
 * `runApplyCPQSelections` resolves that to `cpq_value: "None"` for `removeLeftRight` attributes.
 */
function coerceCpqValueForThreekit(raw: unknown): string | number | null {
  if (raw === undefined || raw === null) return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  if (typeof raw === "string") return raw.trim();
  if (typeof raw === "object" && typeof (raw as { toString?: unknown }).toString === "function") {
    return String(raw).trim();
  }
  return String(raw).trim();
}

function normalizeOnAttributeChangePayload(
  payload: unknown,
  fallbackVarName: string
): { varName: string; value: unknown } {
  if (
    payload != null &&
    typeof payload === "object" &&
    ("value" in (payload as object) || "varName" in (payload as object))
  ) {
    const p = payload as { varName?: unknown; value?: unknown };
    const vn =
      p.varName != null && String(p.varName).trim() !== ""
        ? String(p.varName)
        : fallbackVarName;
    return { varName: vn, value: p.value };
  }
  return { varName: fallbackVarName, value: payload };
}

/**
 * Merges in a default `getCpqAttributeValue` (wired to `CPQJS.getAttributeVal`) for
 * `LeftRightMulti`'s `metadata.leftRightQtyAttr` lookups, unless the caller already supplied one
 * in `applyOptions`. See {@link ApplyCPQSelectionsOptions.getCpqAttributeValue}.
 */
function withDefaultGetCpqAttributeValue(
  cpq: CpqJsAttributeApi,
  applyOptions: ApplyCPQSelectionsOptions | undefined
): ApplyCPQSelectionsOptions | undefined {
  if (applyOptions?.getCpqAttributeValue) return applyOptions;
  if (typeof cpq.getAttributeVal !== "function") return applyOptions;
  const getAttributeVal = cpq.getAttributeVal;
  return {
    ...applyOptions,
    getCpqAttributeValue: (cpqAttribute: string) => {
      try {
        return getAttributeVal(cpqAttribute);
      } catch {
        return undefined;
      }
    },
  };
}

/** Groups linked rows by each distinct `leftRightQtyAttr` name they declare (see below). */
function buildQtyAttrRowsIndex(
  linked: CpqLinkedDisplayAttribute[]
): Map<string, CpqLinkedDisplayAttribute[]> {
  const index = new Map<string, CpqLinkedDisplayAttribute[]>();
  for (const row of linked) {
    for (const qtyAttrRaw of row.leftRightQtyAttrs ?? []) {
      const qtyAttr = qtyAttrRaw?.trim();
      if (!qtyAttr) continue;
      const list = index.get(qtyAttr) ?? [];
      list.push(row);
      index.set(qtyAttr, list);
    }
  }
  return index;
}

function tryOffAttributeChange(
  cpq: CpqJsAttributeApi,
  varName: string,
  handler: (payload: unknown) => void
): void {
  if (typeof cpq.offAttributeChange === "function") {
    cpq.offAttributeChange(varName, handler);
  } else if (typeof cpq.removeAttributeChange === "function") {
    cpq.removeAttributeChange(varName, handler);
  }
}

/**
 * For each linked row’s `cpqAttribute` (and `cpqAttributeAlt`, when present — the alternate CPQ
 * field name used for the same Threekit attribute on a different model), register
 * `CPQJS.onAttributeChange` and push the new value into Threekit via
 * {@link RegisterCpqLinkedAttributeListenersHandle.applyCPQSelections}. Also registers a listener
 * for each row's `leftRightQtyAttrs` (`LeftRightMulti`'s `metadata.leftRightQtyAttr` fields —
 * see `cpq-apply-selections.ts`), which re-applies that row's current bracket value on change so
 * a qty edit alone (without reselecting the bracket value) still updates the Left/Right mapping.
 *
 * Intended for **direct embed** on the Oracle CPQ page (same window as `CPQJS`). For the
 * iframe parent pattern, use `ThreekitCpqIframe` instead (postMessage, not this helper).
 *
 * @example
 * ```ts
 * const listeners = registerCpqLinkedAttributeListeners(threekitCpq, {
 *   applyOptions: { debug: true },
 * });
 * // later, after CPQ update:
 * listeners.setLinkedAttributes(threekitCpq.getCpqLinkedDisplayAttributes());
 * // teardown:
 * listeners.detach();
 * ```
 */
export function registerCpqLinkedAttributeListeners(
  handle: RegisterCpqLinkedAttributeListenersHandle,
  options: RegisterCpqLinkedAttributeListenersOptions = {}
): CpqLinkedAttributeListenersHandle {
  const getCpqJs = options.getCpqJs ?? defaultGetCpqJs;
  const applyOptions = options.applyOptions;

  const registry = new Map<string, (payload: unknown) => void>();
  let activeLinked = new Set<string>();
  let activeQtyAttrs = new Set<string>();

  function isActive(varName: string): boolean {
    return activeLinked.has(varName);
  }

  function isActiveQty(varName: string): boolean {
    return activeQtyAttrs.has(varName);
  }

  function updateActiveFromList(linked: CpqLinkedDisplayAttribute[]): void {
    const next = new Set<string>();
    const nextQty = new Set<string>();
    for (const row of linked) {
      const key = row?.cpqAttribute?.trim();
      if (key) next.add(key);
      // `cpqAttributeAlt` is the CPQ field name used for this same Threekit attribute on a
      // different model — treat it as active too, since only one of the two names will
      // actually exist in CPQ for the currently selected model.
      const altKey = row?.cpqAttributeAlt?.trim();
      if (altKey) next.add(altKey);
      // `leftRightQtyAttr` fields (`LeftRightMulti`) aren't linked via `cpq_attribute`, so they
      // don't belong in `activeLinked` — tracked separately so a change re-applies the owning
      // row's bracket value instead of being (fruitlessly) looked up as its own cpq_attribute.
      for (const qtyAttrRaw of row?.leftRightQtyAttrs ?? []) {
        const qtyAttr = qtyAttrRaw?.trim();
        if (qtyAttr) nextQty.add(qtyAttr);
      }
    }
    activeLinked = next;
    activeQtyAttrs = nextQty;
  }

  function registerForList(linked: CpqLinkedDisplayAttribute[]): void {
    const cpq = getCpqJs();
    if (!cpq || typeof cpq.onAttributeChange !== "function") {
      console.warn(
        "[registerCpqLinkedAttributeListeners] CPQJS.onAttributeChange is not available"
      );
      return;
    }

    updateActiveFromList(linked);

    const seen = new Set<string>();
    for (const row of linked) {
      const varNames = [row?.cpqAttribute?.trim(), row?.cpqAttributeAlt?.trim()].filter(
        (v): v is string => !!v
      );

      for (const varName of varNames) {
        if (seen.has(varName)) continue;
        seen.add(varName);
        if (registry.has(varName)) continue;

        const fixedVarName = varName;
        const onChange = (payload: unknown): void => {
          const norm = normalizeOnAttributeChangePayload(payload, fixedVarName);
          const vn = norm.varName;
          if (!vn || !isActive(vn)) return;
          const coerced = coerceCpqValueForThreekit(norm.value);
          if (coerced === null) return;
          const selections: Record<string, string | number> = {
            [vn]: typeof coerced === "number" ? coerced : String(coerced),
          };
          handle.applyCPQSelections(selections, withDefaultGetCpqAttributeValue(cpq, applyOptions));
        };

        cpq.onAttributeChange(fixedVarName, onChange);
        registry.set(fixedVarName, onChange);
      }
    }

    // `LeftRightMulti` symmetric options (`metadata.leftRightQtyAttr`) determine which side(s) a
    // selected bracket value applies to from a *separate* CPQ qty field's live value (see
    // `cpq-apply-selections.ts`). That qty field isn't itself linked via `cpq_attribute`, so
    // nothing above listens for it — without this, changing qty from 1 to 2 (or back) while
    // keeping the same bracket selection would leave the Left/Right Threekit attributes stale.
    // Listen on every declared qty field and, on change, re-read the owning row's current
    // `cpqAttribute`/`cpqAttributeAlt` value and re-run `applyCPQSelections` with it — that always
    // re-resolves both sides against the live qty value, whether or not it actually changed which
    // side(s) the current selection maps to.
    for (const [qtyVarName, rows] of buildQtyAttrRowsIndex(linked)) {
      if (registry.has(qtyVarName)) continue;

      const onQtyChange = (payload: unknown): void => {
        const norm = normalizeOnAttributeChangePayload(payload, qtyVarName);
        const vn = norm.varName;
        if (!vn || !isActiveQty(vn)) return;

        for (const row of rows) {
          const bracketKeys = [row.cpqAttribute?.trim(), row.cpqAttributeAlt?.trim()].filter(
            (k): k is string => !!k
          );
          for (const bracketKey of bracketKeys) {
            if (typeof cpq.attributeExists === "function" && !cpq.attributeExists(bracketKey)) {
              continue;
            }
            const currentRaw =
              typeof cpq.getAttributeVal === "function" ? cpq.getAttributeVal(bracketKey) : undefined;
            const coerced = coerceCpqValueForThreekit(currentRaw);
            if (coerced === null) continue;
            const selections: Record<string, string | number> = {
              [bracketKey]: typeof coerced === "number" ? coerced : String(coerced),
            };
            handle.applyCPQSelections(selections, withDefaultGetCpqAttributeValue(cpq, applyOptions));
          }
        }
      };

      cpq.onAttributeChange(qtyVarName, onQtyChange);
      registry.set(qtyVarName, onQtyChange);
    }
  }

  const initial =
    options.linked ?? handle.getCpqLinkedDisplayAttributes();
  registerForList(initial);

  return {
    detach() {
      const cpq = getCpqJs();
      for (const [varName, handler] of registry) {
        if (cpq) tryOffAttributeChange(cpq, varName, handler);
      }
      registry.clear();
      activeLinked = new Set();
      activeQtyAttrs = new Set();
    },
    setLinkedAttributes(linked: CpqLinkedDisplayAttribute[]) {
      registerForList(linked);
    },
  };
}
